import { Flag } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";

export const RED_FLAG_REASONS = [
  { value: "retards_recurrents", label: "Retards récurrents" },
  { value: "qualite_insuffisante", label: "Qualité insuffisante" },
  { value: "communication_difficile", label: "Communication difficile" },
  { value: "ghosting_abandon", label: "Ghosting/abandon" },
  { value: "non_respect_brief", label: "Non-respect du brief" },
  { value: "autre", label: "Autre" },
] as const;

export type RedFlagReasonValue = typeof RED_FLAG_REASONS[number]["value"];

export function getRedFlagReasonLabel(value: string | null | undefined): string {
  if (!value) return "Raison non spécifiée";
  const reason = RED_FLAG_REASONS.find(r => r.value === value);
  return reason?.label || value;
}

interface RedFlagBadgeProps {
  reason?: string | null;
  note?: string | null;
  size?: "sm" | "md" | "lg";
  showTooltip?: boolean;
  className?: string;
  "data-testid"?: string;
}

export function RedFlagBadge({ 
  reason, 
  note, 
  size = "sm", 
  showTooltip = true,
  className = "",
  "data-testid": dataTestId
}: RedFlagBadgeProps) {
  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
  };

  const iconSize = sizeClasses[size];
  
  const badge = (
    <Badge 
      variant="destructive" 
      className={`gap-1 px-1.5 py-0.5 ${className}`}
      data-testid={dataTestId || "badge-red-flag"}
    >
      <Flag className={iconSize} />
    </Badge>
  );

  if (!showTooltip) {
    return badge;
  }

  const reasonLabel = getRedFlagReasonLabel(reason);
  
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{badge}</span>
      </TooltipTrigger>
      <TooltipContent 
        side="top" 
        className="max-w-xs"
        data-testid="tooltip-red-flag"
      >
        <div className="space-y-1">
          <p className="font-medium text-destructive-foreground">
            <Flag className="inline h-3 w-3 mr-1" />
            Créateur signalé
          </p>
          <p className="text-sm">
            <span className="font-medium">Raison :</span> {reasonLabel}
          </p>
          {note && (
            <p className="text-sm text-muted-foreground italic">
              {note}
            </p>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

interface RedFlagIconProps {
  className?: string;
  size?: "sm" | "md" | "lg";
}

export function RedFlagIcon({ className = "", size = "sm" }: RedFlagIconProps) {
  const sizeClasses = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };
  
  return (
    <Flag 
      className={`text-destructive ${sizeClasses[size]} ${className}`}
      data-testid="icon-red-flag"
    />
  );
}
